import { useState, useEffect, useContext, useMemo, useCallback, memo } from "react";
import cn from "classnames";
import { useSelector } from "react-redux";
import { FCX, TextInput } from "@datassential/platform-ui-lib";
import { useNavigate } from "react-router-dom";
import { Routes } from "@routes";
import { components } from "@api/api";
import useByoSettings from "@core/apiHooks/useByoSettings";
import { getFormattedDate } from "@core/old_helpers";
import { currentCountrySelector } from "@selectors/countries.selector";
import { useFilterSort } from "@core/hooks/useFilterSort";
import { IconDelete } from "@icons";
import { VirtualizedTable } from "@components/TableNew";
import { TableColumnsContext, TableColumn } from "@components/TableNew/TableColumns";
import TableSortingContext, { SortingOption } from "@components/TableNew/TableSorting";
import Modal from "@components/Modal/Modal";
import ConfirmationPopup from "@components/ConfirmationPopup/ConfirmationPopup";
import { GlobalContext } from "@components/App/App.context";
import Icon from "@components/Icon/Icon";
import "./ByoSettingsModal.scss";
import { IoIosSearch } from "react-icons/io";
import { Sort } from "@models";
import { TitleCell, DescriptionCell, DateCell, CompanyCell } from "./ByoSettingsModalTableCells";
import { BYO_SETTINGS_ID_QUERY_PARAM_NAME, BYO_WORDS_COUNT_QUERY_PARAM_NAME } from "@constants/general";

type ByoSettingOutput = components['schemas']['Application.Common.Models.ByoSettingOutput'];

type ByoSettingWithSortableDate = Omit<ByoSettingOutput, 'createdDate'> & {
    createdDate: number;
    createdDateFormatted: string;
};

const ROW_HEIGHT = 50;

interface ByoSettingsModalProps {
    shown: boolean;
    closeModal: () => void;
}

const ByoSettingsModal: FCX<ByoSettingsModalProps> = ({
    shown,
    closeModal,
}) => {
    const { getRem } = useContext(GlobalContext);
    const navigate = useNavigate();
    const { data, isLoading, deleteSetting } = useByoSettings();
    const currentCountry = useSelector(currentCountrySelector);

    const [removeConfirmationShown, setRemoveConfirmationShown] = useState<boolean>(false);
    const [idForDelete, setIdForDelete] = useState<number>(-1);

    const sortingOptions: SortingOption[] = useMemo(() => [
        { id: 'title', name: 'title', defaultDirection: Sort.Asc },
        { id: 'createdDate', name: 'createdDate', defaultDirection: Sort.Desc },
        { id: 'companyName', name: 'companyName', defaultDirection: Sort.Asc },
    ], []);

    const items: ByoSettingWithSortableDate[] = useMemo(() => {
        if (!data || data.length === 0) {
            return [];
        }

        return data
            .filter(item => !currentCountry || item.countryCode === currentCountry)
            .map((item: ByoSettingOutput) => ({
                ...item,
                createdDate: item.createdDate ? new Date(item.createdDate).getTime() : 0,
                createdDateFormatted: item.createdDate ? getFormattedDate(new Date(item.createdDate), 'MM/DD/YYYY') : '—'
            }));
    }, [data, currentCountry]);

    const {
        data: sortedData,
        query: searchQuery,
        setQuery: setSearchQuery,
        sortingKey,
        setSortingKey,
        sortingDirection,
        setSortingDirection,
    } = useFilterSort<ByoSettingWithSortableDate>(
        items,
        'createdDate',
        Sort.Desc,
        ['title', 'description']
    );

    useEffect(() => {
        if (!shown) {
            setTimeout(() => setSearchQuery(''), 200);
        }
    }, [shown, setSearchQuery]);

    const handleOpenClick = useCallback(
        (id: number, wordsCount: number) => () => {
            navigate(`${Routes.byo}?${BYO_SETTINGS_ID_QUERY_PARAM_NAME}=${id}&${BYO_WORDS_COUNT_QUERY_PARAM_NAME}=${wordsCount}`);
            closeModal();
        },
        [navigate, closeModal]
    );

    const handleRemoveClick = useCallback((id: number) => () => {
        setRemoveConfirmationShown(true);
        setIdForDelete(id);
    }, []);

    const handleRemoveConfirm = useCallback(() => {
        deleteSetting(idForDelete);
    }, [idForDelete, deleteSetting]);

    const renderButtons = useCallback((item: ByoSettingWithSortableDate) => (
        <span className="ByoSettingsModal__buttons-wrap">
            <button
                onClick={handleOpenClick(item.id!, item.words?.length ?? 0)}
                className="ByoSettingsModal__button ByoSettingsModal__button--open"
            >
                Open
            </button>
            <button
                onClick={handleRemoveClick(item.id!)}
                className="ByoSettingsModal__button"
            >
                <Icon>
                    <IconDelete className="ByoSettingsModal__delete-icon"/>
                </Icon>
            </button>
        </span>
    ), [handleOpenClick, handleRemoveClick]);

    const headStyle = useMemo(() => ({ fontSize: 14, fontWeight: 700, color: '#fff' }), []);

    const columns: TableColumn[] = useMemo(() => [
        {
            key: 'title',
            head: 'Name',
            sortKey: 'title',
            Value: ({ index }) => (
                <TitleCell
                    value={sortedData[index]?.title}
                    searchQuery={searchQuery}
                />
            ),
            style: {
                cell: { flex: '0 0 25%' },
                head: headStyle,
            },
        },
        {
            key: 'description',
            head: 'Description',
            Value: ({ index }) => (
                <DescriptionCell
                    value={sortedData[index]?.description}
                    searchQuery={searchQuery}
                    rowHeight={getRem(48)}
                />
            ),
            style: {
                cell: { flex: '0 0 45%' },
                head: headStyle,
            },
        },
        {
            key: 'createdDate',
            head: 'Date',
            sortKey: 'createdDate',
            Value: ({ index }) => (
                <DateCell value={sortedData[index]?.createdDateFormatted} />
            ),
            style: {
                cell: { flex: '0 0 10%' },
                head: headStyle,
            },
        },
        {
            key: 'companyName',
            head: 'Saved by',
            sortKey: 'companyName',
            Value: ({ index }) => (
                <CompanyCell value={sortedData[index]?.companyName}>
                    {renderButtons(sortedData[index])}
                </CompanyCell>
            ),
            style: {
                head: { ...headStyle, flex: '0 0 20%', justifyContent: 'start' },
                cell: { flex: '0 0 20%', justifyContent: 'space-between'},
            },
        },
    ], [sortedData, searchQuery, getRem, renderButtons, headStyle]);

    const hasNoData = sortedData.length === 0 && !isLoading;

    const sortingContextValue = useMemo(() => ({
        value: { id: sortingKey, direction: sortingDirection },
        options: sortingOptions,
        setValue: (value: { id: string | number; direction: Sort }) => {
            setSortingKey(value.id as keyof ByoSettingWithSortableDate);
            setSortingDirection(value.direction);
        }
    }), [sortingKey, sortingDirection, sortingOptions, setSortingDirection, setSortingKey]);

    const columnsContextValue = useMemo(() => ({ data: columns }), [columns]);

    return (
        <>
            <Modal shown={shown} hasOverlay closeModal={closeModal} closeOnOutsideClick>
                <div className={cn('ByoSettingsModal', hasNoData ? 'ByoSettingsModal__not-found' : '')}>
                    <div className="ByoSettingsModal__search-wr">
                        <TextInput
                            className="MenuExamples__results-search"
                            value={searchQuery}
                            setValue={setSearchQuery}
                            placeholder="Search within results..."
                            testId="inputByoSettingsTableSearch"
                        >
                            <IoIosSearch className="MenuExamples__results-search-icon"/>
                        </TextInput>
                    </div>

                    <TableSortingContext.Provider value={sortingContextValue}>
                        <TableColumnsContext.Provider value={columnsContextValue}>
                            <VirtualizedTable
                                rowsCount={sortedData.length}
                                isLoading={isLoading}
                                rowHeight={getRem(ROW_HEIGHT)}
                                showFallback
                                testId="byoSettingsTable"
                            />
                        </TableColumnsContext.Provider>
                    </TableSortingContext.Provider>
                </div>
            </Modal>
            <ConfirmationPopup
                shown={removeConfirmationShown}
                closeModal={() => setRemoveConfirmationShown(false)}
                title="Are you sure you want to delete this report?"
                text={null}
                onConfirm={handleRemoveConfirm}
                submitButtonText="Delete"
            />
        </>
    );
};

export default memo(ByoSettingsModal);